import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

function useScrollToActiveSidebarItem() {
  const scrollAreaRef = useRef<HTMLElement>(null);
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const activeItemRef = useRef<HTMLAnchorElement>(null);
  const { hash, pathname } = useLocation();

  useEffect(() => {
    let defer: ReturnType<typeof setTimeout>;

    if (activeItemRef.current && scrollAreaRef.current && scrollContentRef.current) {
      // When the active item is a child of a nested list, it mounts inside a `display:none;` node.
      // So temporarily set the display of the list, check the activeItem bounding box, then unset the display property.
      const parentList = activeItemRef.current.closest('.rm-Sidebar-list') as HTMLUListElement;

      parentList.style.display = 'flex';
      const { top: activeItemTop, height: activeItemHeight } = activeItemRef.current.getBoundingClientRect();
      parentList.style.display = '';

      // For sidebar scrolling, we should use the sidebar's own scroll area, not the main container
      // The sidebar has its own scrollable area (scrollAreaRef) that we should use for visibility detection
      const sidebarScrollArea = scrollAreaRef.current;
      if (!sidebarScrollArea) return;

      const sidebarRect = sidebarScrollArea.getBoundingClientRect();
      const sidebarViewportTop = sidebarRect.top;
      const sidebarViewportBottom = sidebarRect.bottom;

      // Check if the active item is visible within the sidebar's viewport
      const isInView = activeItemTop >= sidebarViewportTop && activeItemTop + activeItemHeight <= sidebarViewportBottom;

      if (!isInView && scrollContentRef.current) {
        // Calculate the position of the active item relative to the sidebar's scroll area
        const itemOffsetFromSidebar = activeItemTop - sidebarViewportTop;
        const currentScrollTop = sidebarScrollArea.scrollTop;

        // Calculate target scroll position to center the item within the sidebar
        const sidebarHeight = sidebarViewportBottom - sidebarViewportTop;
        const targetScrollTop = currentScrollTop + itemOffsetFromSidebar - sidebarHeight / 2 + activeItemHeight / 2;

        // Ensure we don't scroll beyond the sidebar content bounds
        const maxScrollTop = scrollContentRef.current.scrollHeight - sidebarScrollArea.clientHeight;
        const finalScrollTop = Math.max(0, Math.min(targetScrollTop, maxScrollTop));

        // Defer until the callstack is complete to ensure scrollTop is applied
        defer = setTimeout(() => {
          sidebarScrollArea.scrollTop = finalScrollTop;
        }, 0);
      }
    }

    return () => {
      if (defer) clearTimeout(defer);
    };
  }, [hash, pathname]);

  return { scrollAreaRef, scrollContentRef, activeItemRef };
}

export default useScrollToActiveSidebarItem;
